import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { extractApiError, isRequestCanceled, unwrapApiResponse } from '../api/api';
import { formatDuration } from '../utils/duration';
import { useToast } from './ToastProvider';
import JobDetailPanel from './JobDetailPanel';

const DEFAULT_UPLOAD_CHUNK_SIZE = 10 * 1024 * 1024;

function buildFolderPayload(files, chunkSizeBytes) {
  const normalizedFiles = Array.from(files || []);
  let sourceFolderName = '';

  const manifestFiles = normalizedFiles.map((file, index) => {
    const relativePath = file.webkitRelativePath || file.name;
    if (!sourceFolderName && relativePath.includes('/')) {
      sourceFolderName = relativePath.split('/')[0];
    }
    return {
      clientFile: file,
      fileId: `${index}-${relativePath}`,
      name: file.name,
      relativePath,
      size: file.size,
      type: file.type || 'text/plain',
      totalChunks: Math.max(1, Math.ceil(file.size / chunkSizeBytes))
    };
  });

  return { files: manifestFiles, sourceFolderName };
}

function buildZipPayload(zipFile, chunkSizeBytes) {
  if (!zipFile) {
    return { zipFile: null, sourceFolderName: '' };
  }

  return {
    zipFile: {
      clientFile: zipFile,
      name: zipFile.name,
      relativePath: zipFile.name,
      size: zipFile.size,
      type: zipFile.type || 'application/zip',
      totalChunks: Math.max(1, Math.ceil(zipFile.size / chunkSizeBytes))
    },
    sourceFolderName: zipFile.name || ''
  };
}

export default function IngestionView({ getToken, onJobCreated }) {
  const { showError, showSuccess } = useToast();
  const [configs, setConfigs] = useState([]);
  const [configName, setConfigName] = useState('data_tnfr');
  const [tenantId, setTenantId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedZipFile, setSelectedZipFile] = useState(null);
  const [sourceFolderName, setSourceFolderName] = useState('');
  const [status, setStatus] = useState({ loading: false });
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const folderInputRef = useRef(null);
  const zipInputRef = useRef(null);

  useEffect(() => {
    let timeoutId;
    let activeController;
    let alive = true;

    async function loadData() {
      activeController?.abort?.();
      activeController = new AbortController();
      try {
        const token = await getToken({ skipCache: true });
        const [configResponse, jobsResponse] = await Promise.all([
          api.get('/ingestion/configs', { headers: { Authorization: `Bearer ${token}` }, signal: activeController.signal }),
          api.get('/ingestion/jobs', { headers: { Authorization: `Bearer ${token}` }, signal: activeController.signal })
        ]);

        if (!alive) return;
        const configData = unwrapApiResponse(configResponse);
        const jobsData = unwrapApiResponse(jobsResponse);
        setConfigs(configData?.configs || []);
        if (configData?.configs?.length) {
          setConfigName((current) => current || configData.configs[0].value);
        }
        setJobs(jobsData?.jobs || []);
      } catch (error) {
        if (!alive || isRequestCanceled(error)) return;
        showError(extractApiError(error, 'No se pudo cargar la información de ingesta'));
      } finally {
        if (alive) {
          timeoutId = setTimeout(loadData, 2000);
        }
      }
    }

    loadData();
    return () => {
      alive = false;
      activeController?.abort?.();
      clearTimeout(timeoutId);
    };
  }, [getToken, showError]);

  const selectedCount = selectedFiles.length;
  const selectedFileNames = useMemo(() => selectedFiles.slice(0, 8).map((file) => file.webkitRelativePath || file.name), [selectedFiles]);
  const selectedMode = selectedZipFile ? 'zip' : (selectedFiles.length ? 'folder' : null);

  async function handleSubmit() {
    if (!selectedFiles.length && !selectedZipFile) {
      showError('Selecciona una carpeta o un ZIP con archivos CSV/TXT.');
      return;
    }

    const normalizedTenantId = tenantId.trim().toLowerCase();
    if (!normalizedTenantId) {
      showError('Escribe una Entidad para la carga.');
      return;
    }

    const controller = new AbortController();
    try {
      setStatus({ loading: true, message: 'Preparando upload...' });
      const chunkSizeBytes = DEFAULT_UPLOAD_CHUNK_SIZE;
      const payload = selectedZipFile ? buildZipPayload(selectedZipFile, chunkSizeBytes) : buildFolderPayload(selectedFiles, chunkSizeBytes);
      const { files: folderFiles = [], zipFile = null, sourceFolderName: detectedFolderName } = payload;
      const effectiveSourceFolderName = sourceFolderName || detectedFolderName || (selectedZipFile ? 'archivo.zip' : 'carpeta');

      const createToken = await getToken({ skipCache: true });
      const createResponse = await api.post('/ingestion/jobs', {
        configName,
        tenantId: normalizedTenantId,
        sourceFolderName: effectiveSourceFolderName
      }, {
        headers: {
          Authorization: `Bearer ${createToken}`
        },
        signal: controller.signal
      });

      const createdJob = unwrapApiResponse(createResponse);
      const initToken = await getToken({ skipCache: true });
      const initResponse = await api.post(`/ingestion/jobs/${createdJob?.id}/upload/init`, {
        sourceFolderName: effectiveSourceFolderName,
        files: zipFile ? [] : folderFiles.map((file) => ({
          fileId: file.fileId,
          name: file.name,
          relativePath: file.relativePath,
          size: file.size,
          type: file.type,
          totalChunks: file.totalChunks
        })),
        zipFile: zipFile ? {
          name: zipFile.name,
          size: zipFile.size,
          type: zipFile.type,
          totalChunks: zipFile.totalChunks
        } : null
      }, {
        headers: {
          Authorization: `Bearer ${initToken}`
        },
        signal: controller.signal
      });

      const uploadSession = unwrapApiResponse(initResponse);
      const manifestFiles = uploadSession?.files || [];
      const filesToUpload = zipFile
        ? [{
          manifest: manifestFiles[0],
          clientFile: zipFile.clientFile
        }]
        : manifestFiles.map((manifestFile) => {
          const localFile = folderFiles.find((item) => item.fileId === manifestFile.fileId);
          return {
            manifest: manifestFile,
            clientFile: localFile?.clientFile || null
          };
        });

      let uploadedChunks = 0;
      const totalChunks = filesToUpload.reduce((acc, item) => acc + Number(item.manifest?.totalChunks || 0), 0);

      for (const item of filesToUpload) {
        const manifestFile = item.manifest;
        const clientFile = item.clientFile;
        if (!manifestFile || !clientFile) {
          throw new Error('No se pudo resolver un archivo para la carga por chunks.');
        }

        for (let chunkIndex = 0; chunkIndex < manifestFile.totalChunks; chunkIndex += 1) {
          const chunkStart = chunkIndex * uploadSession.chunkSizeBytes;
          const chunkEnd = Math.min(clientFile.size, chunkStart + uploadSession.chunkSizeBytes);
          const chunkBlob = clientFile.slice(chunkStart, chunkEnd);
          const chunkFormData = new FormData();
          chunkFormData.append('uploadId', uploadSession.uploadId);
          chunkFormData.append('fileId', manifestFile.fileId);
          chunkFormData.append('chunkIndex', String(chunkIndex));
          chunkFormData.append('totalChunks', String(manifestFile.totalChunks));
          chunkFormData.append('chunk', chunkBlob, clientFile.name);

          uploadedChunks += 1;
          setStatus({
            loading: true,
            message: `Subiendo archivo en chunks (${uploadedChunks}/${totalChunks})`
          });

          const chunkToken = await getToken({ skipCache: true });
          await api.post(`/ingestion/jobs/${createdJob?.id}/upload/chunk`, chunkFormData, {
            headers: {
              Authorization: `Bearer ${chunkToken}`,
              'Content-Type': 'multipart/form-data'
            },
            signal: controller.signal
          });
        }
      }

      setStatus({ loading: true, message: 'Ensamblando archivos...' });
      const completeToken = await getToken({ skipCache: true });
      const uploadResponse = await api.post(`/ingestion/jobs/${createdJob?.id}/upload/complete`, {
        uploadId: uploadSession.uploadId
      }, {
        headers: {
          Authorization: `Bearer ${completeToken}`
        },
        signal: controller.signal
      });

      const job = unwrapApiResponse(uploadResponse);
      const successMessage = `Job creado correctamente: ${job?.name || job?.id}`;
      setStatus({ loading: false });
      showSuccess(successMessage, { duration: 4000 });
      resetIngestionForm();

      const refreshToken = await getToken({ skipCache: true });
      const jobsResponse = await api.get('/ingestion/jobs', { headers: { Authorization: `Bearer ${refreshToken}` }, signal: controller.signal });
      setJobs(unwrapApiResponse(jobsResponse)?.jobs || []);
      await onJobCreated?.();
    } catch (error) {
      if (isRequestCanceled(error)) return;
      setStatus({ loading: false });
      showError(extractApiError(error, 'No se pudo crear el job de carga'));
    }
  }

  async function handleCancel(job) {
    try {
      const token = await getToken({ skipCache: true });
      const response = await api.post(`/ingestion/jobs/${job.id}/cancel`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = unwrapApiResponse(response);
      setJobs((current) => current.map((item) => item.id === job.id ? { ...payload, artifacts: [], files: [], totalFiles: 0, successFiles: 0, errorFiles: 0, totalRows: 0, insertedRows: 0, rejectedRows: 0, uploadedFiles: 0, latestMessage: payload.latestMessage || 'Job cancelado. Se reinició la información procesada.' } : item));
      showSuccess(payload?.latestMessage || `Se canceló ${job.name}`, { duration: 3500 });
      resetIngestionForm();
    } catch (error) {
      if (isRequestCanceled(error)) return;
      showError(extractApiError(error, 'No se pudo cancelar el job'));
    }
  }

  function handleFolderChange(event) {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
    setSelectedZipFile(null);
    if (zipInputRef.current) zipInputRef.current.value = '';
    const folderName = files[0]?.webkitRelativePath?.split('/')?.[0] || '';
    setSourceFolderName(folderName);
  }

  function handleZipChange(event) {
    const zipFile = event.target.files?.[0] || null;
    setSelectedZipFile(zipFile);
    setSelectedFiles([]);
    if (folderInputRef.current) folderInputRef.current.value = '';
    setSourceFolderName(zipFile?.name || '');
  }

  function resetIngestionForm() {
    setSelectedFiles([]);
    setSelectedZipFile(null);
    setTenantId('');
    setSourceFolderName('');

    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
    if (zipInputRef.current) {
      zipInputRef.current.value = '';
    }
  }

  return (
    <section className="ingestion-layout">
      <section className="builder-card ingestion-card">
        <div className="table-toolbar compact-toolbar mobile-stack-toolbar">
          <div>
            <div className="brand-badge">Ingesta</div>
            <h2 className="section-title with-margin">Carga masiva</h2>
            <p className="section-subtitle">Selecciona una carpeta o un archivo ZIP y crea un job para cargar la información en segundo plano.</p>
          </div>
        </div>

        <div className="builder-grid ingestion-grid">
          <label className="field">
            <span>Config del worker</span>
            <select value={configName} onChange={(event) => setConfigName(event.target.value)}>
              {configs.map((config) => (
                <option key={config.value} value={config.value}>{config.label}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Entidad (TENANT_ID)</span>
            <input
              type="text"
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value.toLowerCase())}
              placeholder="Entidad"
              autoComplete="off"
            />
          </label>

          {/* <label className="field field-file-upload">
            <span>Carpeta</span>
            <input
              ref={folderInputRef}
              className="ghost-input file-input"
              type="file"
              multiple
              webkitdirectory="true"
              directory="true"
              onChange={handleFolderChange}
            />
          </label> */}

          <label className="field field-file-upload">
            <span>ZIP</span>
            <input
              ref={zipInputRef}
              className="ghost-input file-input"
              type="file"
              accept=".zip"
              onChange={handleZipChange}
            />
          </label>

          <div className="field">
            <span>Resumen</span>
            <div className="ingestion-summary-box">
              <strong>{sourceFolderName || 'Sin fuente seleccionada'}</strong>
              <span>{selectedMode === 'zip' ? '1 archivo ZIP seleccionado' : `${selectedCount} archivo(s) detectados`}</span>
            </div>
          </div>

          <div className="field actions">
            <button className="primary-button full-width" type="button" onClick={handleSubmit} disabled={status.loading}>
              {status.loading ? (status.message || 'Procesando...') : (selectedMode === 'zip' ? 'Procesar ZIP' : 'Procesar carpeta')}
            </button>
          </div>
        </div>

        {selectedMode === 'folder' && selectedFileNames.length ? (
          <div className="ingestion-file-preview">
            {selectedFileNames.map((name) => (
              <span key={name} className="ingestion-file-chip">{name}</span>
            ))}
            {selectedCount > selectedFileNames.length ? <span className="ingestion-file-chip">+{selectedCount - selectedFileNames.length} más</span> : null}
          </div>
        ) : null}

      </section>

      <section className="table-card ingestion-jobs-card">
        <div className="table-toolbar compact-toolbar mobile-stack-toolbar">
          <div>
            <h3 className="section-title">Jobs recientes</h3>
            <p className="section-subtitle">Estado de las últimas cargas registradas</p>
          </div>
        </div>

        <div className="table-shell report-table-shell">
          <table className="report-table responsive-job-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Config</th>
                <th>Entidad</th>
                <th>Estado</th>
                <th>Archivos</th>
                <th>Aceptadas</th>
                <th>Rechazadas</th>
                <th>Duración</th>
                <th>Acciones</th>
                <th>Creado</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length ? jobs.map((job) => {
                return (
                  <tr key={job.id}>
                    <td>{job.name}</td>
                    <td>{job.configName}</td>
                    <td>{job.tenantId || '-'}</td>
                    <td><span className={`status-pill ${job.status}`}>{job.status}</span></td>
                    <td>{job.successFiles}/{job.totalFiles || job.uploadedFiles || 0}</td>
                    <td>{Number(job.insertedRows || 0)}</td>
                    <td>{Number(job.rejectedRows || 0)}</td>
                    <td>{job.durationLabel || formatDuration(job.durationMs)}</td>
                    <td>
                      <div className="row-action-group">
                        <button type="button" className="secondary-button small-action-button" onClick={() => setSelectedJobId(job.id)}>Detalle</button>
                        <button type="button" className="secondary-button small-action-button" onClick={() => handleCancel(job)} disabled={!job.canCancel}>Cancelar</button>
                      </div>
                    </td>
                    <td>{new Date(job.createdAt).toLocaleString()}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="10" className="empty-cell">Sin jobs registrados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <JobDetailPanel jobId={selectedJobId} getToken={getToken} onClose={() => setSelectedJobId(null)} />
    </section>
  );
}
